<script setup>
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import Button from 'next/button/Button.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import AddAutomationDialog from './add/AddAutomationDialog.vue';
import WgptAutomationRow from './components/WgptAutomationRow.vue';
import DeleteAutomationDialog from './delete/DeleteAutomationDialog.vue';
import { picoSearch } from '@chatwoot/pico-search';
import { BaseTable } from 'dashboard/components-next/table';

const router = useRouter();
const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const confirmDialog = ref(null);
const searchQuery = ref('');

const loading = ref({});
const addAutomationDialogRef = ref(false);
const deleteAutomationDialogRef = ref(false);
const toggleModalTitle = ref(t('WGPT_AUTOMATIONS.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('WGPT_AUTOMATIONS.TOGGLE.ACTIVATION_DESCRIPTION')
);
const selectedResponse = ref({});
const fileInput = ref(null);

const records = computed(() => getters['wgptAutomations/getAutomations'].value);
const uiFlags = computed(() => getters['wgptAutomations/getUIFlags'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('wgptAutomations/get');
});

const editAutomation = automation => {
  router.push({
    name: 'wgpt_automations_edit',
    params: {
      automationId: automation.id,
    },
  });
};

const openAddPopup = () => {
  addAutomationDialogRef.value?.open();
};

const openDeletePopup = response => {
  deleteAutomationDialogRef.value?.open(response);
};

const cloneAutomation = async id => {
  try {
    await store.dispatch('wgptAutomations/clone', id);
    useAlert(t('WGPT_AUTOMATIONS.CLONE.API.SUCCESS_MESSAGE'));
    store.dispatch('wgptAutomations/get');
  } catch (error) {
    useAlert(t('WGPT_AUTOMATIONS.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedResponse.value.id] = false;
  }
};

const exportAutomation = async id => {
  try {
    const file = await store.dispatch('wgptAutomations/export', id);
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('WGPT_AUTOMATIONS.EXPORT.API.ERROR_MESSAGE'));
  }
};

const importAutomation = async () => {
  fileInput.value?.click();
};

const handleFileChange = async event => {
  try {
    const file = event.target.files[0];
    if (!file) return;

    const automation = await store.dispatch('wgptAutomations/import', file);
    editAutomation(automation);
  } catch (error) {
    useAlert(t('WGPT_AUTOMATIONS.IMPORT.API.ERROR_MESSAGE'));
  } finally {
    event.target.value = null;
  }
};

const toggleAutomation = async (automation, status) => {
  try {
    toggleModalTitle.value = status
      ? t('WGPT_AUTOMATIONS.TOGGLE.DEACTIVATION_TITLE')
      : t('WGPT_AUTOMATIONS.TOGGLE.ACTIVATION_TITLE');
    toggleModalDescription.value = status
      ? t('WGPT_AUTOMATIONS.TOGGLE.DEACTIVATION_DESCRIPTION', {
          automationName: automation.name,
        })
      : t('WGPT_AUTOMATIONS.TOGGLE.ACTIVATION_DESCRIPTION', {
          automationName: automation.name,
        });

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('wgptAutomations/update', {
        id: automation.id,
        active: !status,
      });
      const message = status
        ? t('WGPT_AUTOMATIONS.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('WGPT_AUTOMATIONS.TOGGLE.ACTIVATION_SUCCESFUL');
      useAlert(message);
    }
  } catch (error) {
    useAlert(t('WGPT_AUTOMATIONS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const tableHeaders = computed(() => {
  return [
    t('WGPT_AUTOMATIONS.LIST.TABLE_HEADER.NAME'),
    // t('WGPT_AUTOMATIONS.LIST.TABLE_HEADER.DESCRIPTION'),
    t('WGPT_AUTOMATIONS.LIST.TABLE_HEADER.TYPE'),
    t('WGPT_AUTOMATIONS.LIST.TABLE_HEADER.ACTIVE'),
    t('WGPT_AUTOMATIONS.LIST.TABLE_HEADER.UPDATED_ON'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('WGPT_AUTOMATIONS.LIST.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('WGPT_AUTOMATIONS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('WGPT_AUTOMATIONS.HEADER')"
        :description="$t('WGPT_AUTOMATIONS.DESCRIPTION')"
        :search-placeholder="$t('WGPT_AUTOMATIONS.SEARCH_PLACEHOLDER')"
        feature-name="wgpt_automations"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('WGPT_AUTOMATIONS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center flex-shrink-0 gap-4">
            <Button
              icon="i-lucide-import"
              :label="$t('WGPT_AUTOMATIONS.HEADER_BTN_TEXTS.IMPORT')"
              :disabled="uiFlags.isImporting"
              :is-loading="uiFlags.isImporting"
              size="sm"
              slate
              @click="importAutomation"
            />
            <Button
              :label="$t('WGPT_AUTOMATIONS.HEADER_BTN_TEXTS.CREATE')"
              size="sm"
              @click="openAddPopup"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery
            ? $t('WGPT_AUTOMATIONS.NO_RESULTS')
            : $t('WGPT_AUTOMATIONS.LIST.404')
        "
      >
        <template #row="{ items }">
          <WgptAutomationRow
            v-for="automation in items"
            :key="automation.id"
            :automation="automation"
            :loading="loading[automation.id]"
            @edit="editAutomation(automation)"
            @clone="cloneAutomation(automation.id)"
            @delete="openDeletePopup(automation)"
            @toggle="toggleAutomation(automation, automation.active)"
            @export="exportAutomation(automation.id)"
          />
        </template>
      </BaseTable>
    </template>

    <AddAutomationDialog ref="addAutomationDialogRef" />

    <DeleteAutomationDialog ref="deleteAutomationDialogRef" />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />

    <input
      ref="fileInput"
      type="file"
      accept=".wgpt"
      class="hidden"
      @change="handleFileChange"
    />
  </SettingsLayout>
</template>
